'use client';

import { blackTransparentLoaderModalAtom, centerToastModalAtom } from '@/atom/modal';
import { SurveyCGType } from '@/types/graphql/root';
import client from '@/utils/apollo-client';
import { GetHolidaysQuery } from '@/utils/gql';
import dayjs from 'dayjs';
import { useSetAtom } from 'jotai';
import isDate from 'lodash/isDate';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import SurveyTopBar from '../../survey/survey-top-bar';
import DateRangeCalendar from '../../survey/date-range-calendar';
import TimePrimary2020 from '@svg/survey/when/time_primary_20_20.svg';
import TimeSelectBottomModal from '../../survey/time-select-bottom-modal';
import { modifySurveyDate } from '@/utils/actions';
import useActionHandler from '@/app/hooks/useActionHandler';

const DEFAULT_START_TIME = '11:00';
const DEFAULT_END_TIME = '20:00';
const TIMES = [
  { label: '12:00  AM', value: '00:00' },
  { label: '1:00  AM', value: '01:00' },
  { label: '2:00  AM', value: '02:00' },
  { label: '3:00  AM', value: '03:00' },
  { label: '4:00  AM', value: '04:00' },
  { label: '5:00  AM', value: '05:00' },
  { label: '6:00  AM', value: '06:00' },
  { label: '7:00  AM', value: '07:00' },
  { label: '8:00  AM', value: '08:00' },
  { label: '9:00  AM', value: '09:00' },
  { label: '10:00  AM', value: '10:00' },
  { label: '11:00  AM', value: '11:00' },
  { label: '12:00  PM', value: '12:00' },
  { label: '1:00  PM', value: '13:00' },
  { label: '2:00  PM', value: '14:00' },
  { label: '3:00  PM', value: '15:00' },
  { label: '4:00  PM', value: '16:00' },
  { label: '5:00  PM', value: '17:00' },
  { label: '6:00  PM', value: '18:00' },
  { label: '7:00  PM', value: '19:00' },
  { label: '8:00  PM', value: '20:00' },
  { label: '9:00  PM', value: '21:00' },
  { label: '10:00  PM', value: '22:00' },
  { label: '11:00  PM', value: '23:00' },
];

export default function ModifySurveyDateModal({
  isVisible,
  setIsVisible,
  trip,
  editUser,
  iamEdit,
}: {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  trip: SurveyCGType;
  editUser: string | null;
  iamEdit: boolean;
}) {
  const router = useRouter();
  const setBlackTransparentLoaderModal = useSetAtom(blackTransparentLoaderModalAtom);
  const setCenterToastModal = useSetAtom(centerToastModalAtom);

  const {
    execute: executeModifySurveyDate,
    data: modifySurveyDateData,
    isLoading,
  } = useActionHandler({
    action: modifySurveyDate,
    key: trip.id,
  });

  const [from, setFrom] = useState<Date | undefined>(
    trip.fromDateTime ? dayjs(trip.fromDateTime).startOf('day').toDate() : undefined,
  );
  const [to, setTo] = useState<Date | undefined>(
    trip.toDateTime ? dayjs(trip.toDateTime).startOf('day').toDate() : undefined,
  );
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    trip.fromDateTime ? dayjs(trip.fromDateTime).startOf('month').toDate() : new Date(),
  );
  const [holidays, setHolidays] = useState<{ date: string; name: string }[]>([]);

  const [startTime, setStartTime] = useState<string>(
    trip.fromDateTime ? dayjs(trip.fromDateTime).format('HH:mm') : DEFAULT_START_TIME,
  );
  const [endTime, setEndTime] = useState<string>(
    trip.toDateTime ? dayjs(trip.toDateTime).format('HH:mm') : DEFAULT_END_TIME,
  );
  const [timeType, setTimeType] = useState<'start' | 'end' | undefined>(undefined);

  const fromReady = useMemo(() => isDate(from), [from]);
  const toReady = useMemo(() => isDate(to), [to]);
  const timeVisible = useMemo(() => {
    return isDate(from) && isDate(to) && dayjs(from).format('YYYY-MM-DD') !== dayjs(to).format('YYYY-MM-DD');
  }, [from, to]);

  const startTimeLabel = useMemo(() => {
    return TIMES.find((time) => time.value === startTime)?.value;
  }, [startTime]);
  const endTimeLabel = useMemo(() => {
    return TIMES.find((time) => time.value === endTime)?.value;
  }, [endTime]);

  const onSelectTime = useCallback((time: string, timeType: 'start' | 'end') => {
    if (timeType === 'start') {
      setStartTime(time);
    } else {
      setEndTime(time);
    }
  }, []);

  useEffect(() => {
    if (!toReady) return;
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
  }, [toReady]);

  const year = useMemo(() => {
    return dayjs(calendarMonth).format('YYYY');
  }, [calendarMonth]);

  const fromToGap = useMemo(() => {
    const fromDayjs = dayjs(trip.fromDateTime).startOf('day');
    const toDayjs = dayjs(trip.toDateTime).startOf('day');
    const gap = toDayjs.diff(fromDayjs, 'day');

    return gap;
  }, [trip]);

  useEffect(() => {
    client
      .query({
        query: GetHolidaysQuery,
        fetchPolicy: 'cache-first',
        variables: {
          start: `${year}-01-01`,
          end: `${year}-12-31`,
        },
      })
      .then(({ data }) => {
        setHolidays(data.holidays);
      });
  }, [year]);

  const onClickSubmit = useCallback(async () => {
    if (!trip || !from || !to) return;

    if (!iamEdit && editUser) {
      setCenterToastModal({
        text: `지금 ${editUser}님이 편집 중이에요!\n잠시만 기다려주세요.`,
        renderingTime: 2000,
        iconHidden: true,
      });
      return;
    }

    const fromDateTime = `${dayjs(from).format('YYYY-MM-DD')} ${startTime}`;
    const toDateTime = `${dayjs(to).format('YYYY-MM-DD')} ${endTime}`;

    await executeModifySurveyDate(trip.id, fromDateTime, toDateTime);
  }, [trip, from, to, startTime, endTime, iamEdit, editUser]);

  useEffect(() => {
    if (modifySurveyDateData) {
      router.back();
    }
  }, [modifySurveyDateData]);

  useEffect(() => {
    setBlackTransparentLoaderModal(isLoading);
  }, [isLoading]);

  if (!isVisible) return null;

  return (
    <div className='poi-modal-in-main fixed w-full max-w-480pxr h-dvh top-0 bg-white z-10'>
      <SurveyTopBar
        title='날짜 선택'
        onClickBack={() => {
          router.back();
        }}
      />

      <div id='modify-survey-date-modal' className='h-[calc(100dvh-56px)] overflow-y-scroll'>
        <h5 className='flex flex-row items-center py-24pxr px-20pxr gap-12pxr'>
          <p className='text-custom-black heading-18-b'>언제 떠나시나요?</p>
          <span className='text-point-400 body-14-r'>*최대 5박 6일 선택 가능</span>
        </h5>

        <div className='relative w-full'>
          <DateRangeCalendar
            startDate={from ?? null}
            endDate={to ?? null}
            onStartDateChange={(date) => {
              if (date) {
                setFrom(date);
                setTo(dayjs(date).add(fromToGap, 'day').toDate());
                document.getElementById('modify-survey-date-modal')?.scrollTo({
                  top: document.body.scrollHeight,
                  behavior: 'smooth',
                });
              }
            }}
            onEndDateChange={(date) => {}}
            focus={'startDate'}
            onFocusChange={() => {}}
            maximumLength={5}
            minimumDate={new Date()}
            month={calendarMonth}
            onMonthChange={(date) => {
              if (!date) return;
              setCalendarMonth(date);
            }}
            holidays={holidays}
          />
        </div>

        {(fromReady || toReady) && (
          <div className='flex flex-col border-t border-gray-200 mt-20pxr mx-20pxr py-24pxr gap-12pxr'>
            {fromReady && (
              <div className='flex flex-col items-start w-full'>
                <span className='text-gray-600 sub-heading-14-m'>일정 시작</span>
                <div className='flex flex-row items-center justify-between w-full h-49pxr'>
                  <span className='text-custom-black sub-heading-16-m'>
                    {from && dayjs(from).locale('ko').format('YYYY.MM.DD (ddd)')}
                  </span>
                  {timeVisible && (
                    <button
                      className='flex flex-row items-center justify-center border border-gray-200 px-12pxr py-14pxr gap-6pxr rounded-6pxr'
                      onClick={() => {
                        setTimeType('start');
                      }}
                    >
                      <TimePrimary2020 className='flex-none' />
                      <p className='text-gray-800 sub-heading-16-m'>{startTimeLabel}</p>
                    </button>
                  )}
                </div>
              </div>
            )}
            {toReady && (
              <div className='flex flex-col items-start w-full'>
                <span className='text-gray-600 sub-heading-14-m'>일정 종료</span>
                <div className='flex flex-row items-center justify-between w-full h-49pxr'>
                  <span className='text-custom-black sub-heading-16-m'>
                    {to && dayjs(to).locale('ko').format('YYYY.MM.DD (ddd)')}
                  </span>
                  {timeVisible && (
                    <button
                      className='flex flex-row items-center justify-center border border-gray-200 px-12pxr py-14pxr gap-6pxr rounded-6pxr'
                      onClick={() => {
                        setTimeType('end');
                      }}
                    >
                      <TimePrimary2020 className='flex-none' />
                      <p className='text-gray-800 sub-heading-16-m'>{endTimeLabel}</p>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <div className='h-120pxr'></div>
      </div>

      <footer
        className='fixed bottom-0 z-[2] w-full bg-white h-100pxr rounded-t-16pxr px-20pxr pt-16pxr pb-34pxr max-w-480pxr'
        style={{ boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)' }}
      >
        <button
          className={`w-full h-full rounded-8pxr ${fromReady && toReady ? 'bg-custom-black' : 'bg-gray-300'}`}
          disabled={!(fromReady && toReady)}
          onClick={() => onClickSubmit()}
        >
          <p className='text-white'>변경하기</p>
        </button>
      </footer>

      <TimeSelectBottomModal
        currentSelectedTime={timeType === 'start' ? startTime : endTime}
        timeType={timeType}
        setTimeType={setTimeType}
        onSelectTime={onSelectTime}
      />
    </div>
  );
}
